import React, { useMemo, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import CustomTextInput from '../../../components/CustomTextInput';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import { checkEmailStatus } from '../dataHandlers/checkEmailStatusHandler';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch } from 'react-redux';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { useRevalidateFormOnLanguageChange } from '../../../hooks/useRevalidateFormOnLanguageChange';
import appConfig from '../../../config/appConfig';
import { useSignupReferral } from '../hooks/useSignupReferral';
import SignupBrandLogos from './SignupBrandLogos';

export const createEmailGateSchema = (t) =>
  Yup.object().shape({
    email: Yup.string().email(t.invalid_email_format).required(t.email_required),
  });

const EmailGate = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { signupPath } = useSignupReferral();
  const { users: u, language } = useTranslatedMessages();
  const t = u.email_gate ?? {};

  const schema = useMemo(
    () =>
      createEmailGateSchema({
        invalid_email_format: t.validations?.invalid_email_format ?? 'Invalid email format',
        email_required: t.validations?.email_required ?? 'Email is required',
      }),
    [t.validations],
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
  });

  useRevalidateFormOnLanguageChange(language, trigger, errors);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const response = await checkEmailStatus(data.email);
      const { status, submittedAt } = response;

      switch (status) {
        case 'ACTIVE':
          dispatch(
            showSnackbar({
              message:
                t.snackbar?.user_exists ??
                'User already exists with the provided email. Please try again with a different email or try logging in.',
              severity: 'error',
            }),
          );
          break;

        case 'PENDING_ACTIVATION':
          navigate(signupPath, {
            replace: true,
            state: {
              stage: 'waiting',
              email: data.email,
              submittedAt: submittedAt || new Date().toISOString(),
            },
          });
          break;

        case 'NOT_FOUND':
        default:
          navigate(signupPath, {
            replace: true,
            state: {
              stage: 'signup',
              email: data.email,
            },
          });
          break;
      }
    } catch (error) {
      console.error('Error checking email status:', error);
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.errors?.message ||
            t.snackbar?.failed_check_email_status ||
            'Failed to check email status. Please try again.',
          severity: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SignupBrandLogos />
      <Stack spacing={2}>
        <CustomTextInput name="email" label={t.fields?.email ?? 'Email'} control={control} errors={errors} />
        <Button
          type="button"
          variant="contained"
          className="submit"
          onClick={handleSubmit(onSubmit)}
          disabled={loading}
        >
          {loading ? (t.actions?.checking ?? 'Checking...') : (t.actions?.signup ?? 'SIGN UP')}
        </Button>
        <Typography variant="body2" sx={{ textAlign: 'center', fontWeight: 700 }}>
          {t.links?.already_have_account ?? 'Already have an account?'}{' '}
          <Link to="/login" style={{ textDecoration: 'none', fontWeight: 400 }}>
            {t.links?.sign_in ?? 'Sign In'}
          </Link>
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'center', fontWeight: 700 }}>
          {t.links?.facing_issues ?? 'Facing Issues?'}{' '}
          <a href={`mailto:${appConfig.brandEmail}`} style={{ textDecoration: 'none', fontWeight: 400 }}>
            {t.links?.contact_support ?? 'Contact Support'}
          </a>
        </Typography>
      </Stack>
    </>
  );
};

export default EmailGate;
